/*
 * File: Resources.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 11, 2025 at 2:28 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Masonry from 'react-responsive-masonry';

// Services
import { fetchResources } from '../../services/resource';

// Store
import { updateFeaturedResources } from '../../store/slices/resource/resource.slice';

// Components
import { ResourceCard } from '../ResourceCard';
import { ResponsiveMasonry } from '../ResponsiveMasonry';

// Constants
import { SOURCES } from '../../../constants/base.constants';
import { panelMasonryBreakpoints } from '../../styles/constants';

// Styles
import * as S from './Resources.styles';


/**
 * Component
 */

export const Resources = ({
	className
}) => {

	// Get resources from hook
	const {
		featuredStatus,
		featuredItems: featuredResources,
	} = useSelector((state) => state.resource);

	// Use hooks
	const dispatch = useDispatch();

	// Handle fetch featured resources
	const fetchFeaturedResources = async () => {

		// Fetch resources
		const { data: { result: fetchedResources } } = await fetchResources({
			page: 0,
			isFeatured: true
		});

		// Update resources
		dispatch(updateFeaturedResources(fetchedResources));
	};

	// Handle actions on component load and status changed
	useEffect(() => {

		// Fetch featured resources if necessary
		if (featuredStatus !== 'final') {

			// Fetch featured resources
			fetchFeaturedResources();
		}
	}, [featuredStatus]);

	// Render component
	return (
		<S.Wrapper className={className}>
			<S.ResourcesPanel>
				<ResponsiveMasonry defaultBreakpoint={2} breakpoints={panelMasonryBreakpoints}>
					<Masonry gutter="22px">
						{featuredResources.map((resource, index) => (
							<ResourceCard
								key={resource.resource_id}
								resource={resource}
								source={SOURCES.SHARE}
								index={index}
							/>
						))}
					</Masonry>
				</ResponsiveMasonry>
			</S.ResourcesPanel>
		</S.Wrapper>
	);
};


/**
 * Configuration
 */

Resources.displayName = 'Resources';
Resources.propTypes = {
	className: PropTypes.string
};
Resources.defaultProps = {
	className: null,
};
